import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, Anchor, ChevronDown, Car, Navigation, Phone, Mail, Instagram, Facebook, ArrowUpRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logoIcon from "@/assets/favicon-skiathos-transparent.png";

type SubLink = { labelKey: string; href: string; descKey?: string; icon?: typeof Car };
type NavItem = { labelKey: string; shortKey?: string; shortFallback?: string; href?: string; activeMatch?: string[]; children?: SubLink[] };

const navLinks: NavItem[] = [
  {
    labelKey: "nav.cruises",
    shortKey: "nav.short.cruises",
    shortFallback: "Cruises",
    activeMatch: ["/skiathos-daily-cruises", "/private-boat-rentals-in-skiathos"],
    children: [
      { labelKey: "nav.dailyCruises", href: "/skiathos-daily-cruises", descKey: "nav.dailyCruisesDesc", icon: Anchor },
      { labelKey: "nav.privateBoatRentals", href: "/private-boat-rentals-in-skiathos", descKey: "nav.privateBoatRentalsDesc", icon: Compass },
    ],
  },
  { labelKey: "nav.accommodations", shortKey: "nav.short.stay", shortFallback: "Stay", href: "/accommodations", activeMatch: ["/accommodations"] },
  {
    labelKey: "nav.transportation",
    shortKey: "nav.short.transfers",
    shortFallback: "Transfers",
    activeMatch: ["/rentals", "/private-transfers"],
    children: [
      { labelKey: "nav.rentals", href: "/rentals", descKey: "nav.rentalsDesc", icon: Car },
      { labelKey: "nav.privateTransfers", href: "/private-transfers", descKey: "nav.transfersDesc", icon: Navigation },
    ],
  },
  { labelKey: "nav.thingsToDo", shortKey: "nav.short.explore", shortFallback: "Explore", href: "/activities", activeMatch: ["/activities"] },
  { labelKey: "nav.services", shortKey: "nav.short.services", shortFallback: "Services", href: "/services", activeMatch: ["/services"] },
];

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  // Routes whose hero is light/cream — navbar must use solid (scrolled) style for legibility
  const LIGHT_HERO_ROUTES = ["/contact", "/about"];
  const forceSolid = LIGHT_HERO_ROUTES.some((p) => currentPath === p || currentPath.startsWith(p + "/"));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const isActive = (item: NavItem) => {
    if (!item.activeMatch) return false;
    return item.activeMatch.some((p) => currentPath === p || currentPath.startsWith(p + "/"));
  };
  const isCinematicRoute = [
    "/",
    "/skiathos-daily-cruises",
    "/private-boat-rentals-in-skiathos",
    "/accommodations",
    "/activities",
    "/skiathos-boat-party",
    "/private-transfers",
    "/services"
  ].some(route => currentPath === route || currentPath.startsWith(route + "/"));

  const isTransparent = isCinematicRoute && !isScrolled;
  
  const textColorClass = isTransparent ? "text-white" : "text-ocean-deep";
  const textMutedClass = isTransparent ? "text-white/80" : "text-ocean-deep/80";
  const logoFilter = isTransparent ? "brightness-0 invert drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" : "drop-shadow-md";

  return (
    <header className={`${isCinematicRoute ? "fixed" : "sticky"} top-0 left-0 right-0 z-50 transition-all duration-300 ${isTransparent ? 'bg-transparent border-transparent shadow-none' : 'bg-white border-b border-gray-100 shadow-sm'}`}>
      {/* Main nav */}
      <nav className="w-full">
        <div className="w-full max-w-[1280px] mx-auto flex items-center justify-between px-4 md:px-6 lg:px-8 py-3 lg:h-[72px]">
          <a href="/" className="flex items-center gap-3 transition-transform duration-300 hover:scale-[1.02] shrink-0 lg:mr-8">
            <img
              src={logoIcon}
              alt="Skiathos Travellers Logo"
              className={`object-contain h-10 w-10 lg:h-12 lg:w-12 transition-all duration-300 ${logoFilter}`}
            />
            <div className="flex flex-col items-start justify-center">
              <span className={`font-display font-semibold leading-none tracking-tight text-lg lg:text-xl transition-colors duration-300 ${textColorClass}`}>
                Skiathos Travellers
              </span>
              <span className={`hidden md:block font-body text-[9px] font-medium uppercase tracking-[0.25em] mt-1 transition-colors duration-300 ${isTransparent ? 'text-white/80' : 'text-ocean-deep/60'}`}>
                Local Travel Concierge
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((link) => {
              const active = isActive(link);
              const label = link.shortKey ? t(link.shortKey, link.shortFallback) : t(link.labelKey);
              return (
                <div key={link.labelKey} className="relative group">
                  {link.children ? (
                    <>
                      <button
                        type="button"
                        className={`relative py-2 text-[13px] font-body font-semibold uppercase tracking-wider transition-colors duration-300 flex items-center gap-1.5 ${active ? textColorClass : `${textMutedClass} hover:text-secondary`
                          }`}
                      >
                        {label}
                        <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform group-hover:rotate-180" />
                        <span
                          className={`absolute -bottom-1 left-0 h-[2px] bg-secondary rounded-full transition-all duration-300 ease-out ${active ? "w-[calc(100%-1.25rem)] opacity-100" : "w-0 opacity-0 group-hover:w-[calc(100%-1.25rem)] group-hover:opacity-100"
                            }`}
                        />
                      </button>

                      {/* Dropdown */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-white/40 overflow-hidden min-w-[280px] transform origin-top scale-95 group-hover:scale-100 transition-transform duration-300">
                          <div className="p-2 relative">
                            {/* Inner glow effect for premium feel */}
                            <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent pointer-events-none rounded-2xl" />
                            
                            {link.children.map((sub) => {
                              const Icon = sub.icon;
                              return (
                                <a
                                  key={sub.href}
                                  href={sub.href}
                                  className="relative flex items-start gap-3 p-3 rounded-xl hover:bg-ocean-deep/5 transition-all duration-300 group/item z-10"
                                >
                                  {Icon && (
                                    <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-secondary group-hover/item:shadow-[0_4px_12px_rgba(242,166,58,0.3)] transition-all duration-300">
                                      <Icon className="h-4 w-4 text-secondary group-hover/item:text-white transition-colors duration-300" />
                                    </div>
                                  )}
                                  <div className="flex flex-col pt-0.5">
                                    <span className="font-body text-[13px] font-bold text-ocean-deep group-hover/item:text-primary transition-colors duration-300">{t(sub.labelKey)}</span>
                                    {sub.descKey && (
                                      <span className="font-body text-[11px] text-ocean-deep/60 mt-0.5">{t(sub.descKey)}</span>
                                    )}
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <a
                      href={link.href}
                      className={`relative py-2 text-[13px] font-body font-semibold uppercase tracking-wider transition-colors duration-300 flex items-center ${active ? textColorClass : `${textMutedClass} hover:text-secondary`
                        }`}
                    >
                      {label}
                      <span
                        className={`absolute -bottom-1 left-0 h-[2px] bg-secondary rounded-full transition-all duration-300 ease-out ${active ? "w-full opacity-100" : "w-0 opacity-0 group-hover:w-full group-hover:opacity-100"
                          }`}
                      />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-4 lg:ml-8">
            <LanguageSwitcher />
            <a href="/skiathos-boat-party" className="relative group">
              {/* Subtle glow halo */}
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-primary via-secondary to-primary opacity-50 blur-md group-hover:opacity-90 transition-opacity duration-500" />
              <Button
                size="sm"
                className="relative h-8 font-body font-bold text-[9px] tracking-widest uppercase flex items-center gap-1.5 rounded-full bg-gradient-to-r from-primary to-secondary text-white px-3.5 border border-white/20 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              >
                <Anchor className="h-3.5 w-3.5 text-white" />
                {t("nav.oasisParty")}
              </Button>
            </a>
          </div>

          {/* Mobile toggle */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative p-2.5 rounded-full transition-all active:scale-95 bg-ocean-deep/5 hover:bg-ocean-deep/10 text-ocean-deep"
              aria-label={t("nav.toggleMenu")}
            >
              <div className="relative h-5 w-5">
                <Menu className={`h-5 w-5 absolute inset-0 transition-all duration-300 ${textColorClass} ${isOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}`} />
                <X className={`h-5 w-5 absolute inset-0 transition-all duration-300 ${textColorClass} ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu - Fullscreen Overlay */}
      <div
        className={`lg:hidden fixed inset-0 top-[68px] z-[60] transition-opacity duration-200 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
      >
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-ocean-deep via-ocean-deep to-primary" />

        {/* Decorative elements (reduced blur to avoid iOS Safari rendering bugs) */}
        <div className="absolute top-10 -right-20 w-72 h-72 bg-secondary/10 rounded-full blur-3xl transform-gpu animate-pulse-slow" />
        <div className="absolute bottom-20 -left-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl transform-gpu animate-pulse-slow" />

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        {/* Content */}
        <div className="relative h-full overflow-y-auto pb-safe overscroll-contain">
          <div className="px-6 pt-8 pb-32 flex flex-col min-h-full">

            {/* Eyebrow */}
            <div className={`flex items-center gap-2 mb-6 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
              <Compass className="h-3.5 w-3.5 text-secondary" />
              <span className="font-body text-[10px] font-bold tracking-[0.3em] uppercase text-secondary">{t("nav.explore")}</span>
            </div>

            {/* Nav Links */}
            <nav className="flex flex-col gap-1 mb-8">
              {navLinks.map((link) => (
                <div
                  key={link.labelKey}
                >
                  {link.children ? (
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => setMobileExpanded(mobileExpanded === link.labelKey ? null : link.labelKey)}
                        className="group w-full flex items-center justify-between py-3 border-b border-white/10"
                      >
                        <span className="font-display text-3xl font-bold text-white tracking-tight group-hover:text-secondary transition-colors">
                          {t(link.labelKey)}
                        </span>
                        <ChevronDown className={`h-5 w-5 text-white/60 transition-transform duration-300 ${mobileExpanded === link.labelKey ? 'rotate-180 text-secondary' : ''}`} />
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === link.labelKey ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                        <div className="pl-4 border-l-2 border-secondary/60 flex flex-col gap-1 py-2">
                          {link.children.map((sub) => {
                            const Icon = sub.icon;
                            return (
                              <a
                                key={sub.href}
                                href={sub.href}
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-white/10 transition-colors group/sub"
                              >
                                {Icon && (
                                  <div className="h-9 w-9 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                                    <Icon className="h-4 w-4 text-secondary" />
                                  </div>
                                )}
                                <div className="flex flex-col flex-1">
                                  <span className="font-body text-sm font-bold text-white">{t(sub.labelKey)}</span>
                                  {sub.descKey && (
                                    <span className="font-body text-[11px] text-white/60 mt-0.5">{t(sub.descKey)}</span>
                                  )}
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-white/40 group-hover/sub:text-secondary group-hover/sub:translate-x-0.5 group-hover/sub:-translate-y-0.5 transition-all" />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={link.href}
                      onClick={() => setIsOpen(false)}
                      className="group flex items-center justify-between py-3 border-b border-white/10"
                    >
                      <span className="font-display text-3xl font-bold text-white tracking-tight group-hover:text-secondary transition-colors">
                        {t(link.labelKey)}
                      </span>
                      <ArrowUpRight className="h-5 w-5 text-white/40 group-hover:text-secondary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                    </a>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="mb-8">
              <a href="/skiathos-boat-party" onClick={() => setIsOpen(false)} className="relative group block">
                <div className="absolute -inset-1 bg-gradient-to-r from-secondary via-yellow-400 to-accent rounded-2xl blur-md opacity-70 group-hover:opacity-100 transition duration-500 animate-pulse-slow"></div>
                <div className="relative w-full rounded-2xl bg-gradient-to-br from-black to-ocean-deep p-5 flex items-center justify-between border border-white/10 overflow-hidden">
                  <div className="flex items-center gap-3 relative z-10">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-secondary to-yellow-500 flex items-center justify-center shadow-lg">
                      <Anchor className="h-6 w-6 text-black" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-secondary">{t("nav.featured")}</span>
                      <span className="font-display text-xl font-bold text-white">{t("nav.oasisBoatParty")}</span>
                    </div>
                  </div>
                  <ArrowUpRight className="h-6 w-6 text-white relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </a>
            </div>

            {/* Contact + Social */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="h-px flex-1 bg-white/20" />
                <span className="font-body text-[10px] font-bold tracking-[0.25em] uppercase text-white/50">{t("nav.getInTouch")}</span>
                <div className="h-px flex-1 bg-white/20" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a href="tel:+306900000000" className="flex items-center gap-2.5 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <Phone className="h-4 w-4 text-secondary flex-shrink-0" />
                  <span className="font-body text-xs font-semibold text-white">{t("nav.callUs")}</span>
                </a>
                <a href="/contact" onClick={() => setIsOpen(false)} className="flex items-center gap-2.5 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <Mail className="h-4 w-4 text-secondary flex-shrink-0" />
                  <span className="font-body text-xs font-semibold text-white">{t("nav.email")}</span>
                </a>
              </div>

              <div className="flex items-center justify-center gap-3 pt-2">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="h-11 w-11 rounded-full bg-white/10 hover:bg-secondary hover:text-black border border-white/10 flex items-center justify-center text-white transition-all hover:scale-110">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="h-11 w-11 rounded-full bg-white/10 hover:bg-secondary hover:text-black border border-white/10 flex items-center justify-center text-white transition-all hover:scale-110">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="https://www.tiktok.com/@skiathostravellers?_t=ZN-8ub3In6zYPe&_r=1" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="h-11 w-11 rounded-full bg-white/10 hover:bg-secondary hover:text-black border border-white/10 flex items-center justify-center text-white transition-all hover:scale-110">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52V6.84a4.84 4.84 0 0 1-1-.15z" /></svg>
                </a>
              </div>

              <p className="text-center font-body text-[10px] text-white/40 tracking-[0.2em] uppercase mt-2">
                Skiathos Travellers © {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
